import shoePrintSrc from "../assets/images/shoe_print.png";

/**
 * Symbolic constants for different body positions used in sit-to-stand assessment.
 */
export const Position = {
  Standing: Symbol(0),
  Sitting: Symbol(1),
  Midway: Symbol(2),
  None: Symbol(3),
};

/**
 * The SitStandProcessing class handles core logic for detecting and counting
 * sit-to-stand repetitions. It stores references for sitting/standing thresholds,
 * and manages a bounding box on the display to track the user's location.
 */
export class SitStandProcessing {
  constructor() {
    // Flags and reference counters
    this.__stand_head_detected = false;
    this.Person_Position = Position.None;
    this.sitting_count = 0;
    this.standing_count = 0;

    // Detailed rep info
    this.standing_time = [];
    this.sitting_position = [];
    this.standing_position = [];
    this.last_position = Position.Sitting;

    // Reference lines for standing and sitting
    this.standing_reference_point = null;
    this.sitting_reference_point = null;

    // Internal measurement and ratio checks
    this.patient_femur_2_tibia_ratio = 1000;
    this.patient_in_sitting_region = true;
    this.patient_stood_up = false;

    // Timestamp data
    this.assessment_start_time = null;
    this.assessment_end_time = null;
    this.individual_rep_start_time_previous = null;
    this.individual_rep_start_time_current = null;
    this.individual_rep_end_time = null;
    this.individual_rep = [];
    this.total_time = 0;
    this.calibration_error_occured = false;

    // Canvas references
    this.canvas = document.getElementById("output");
    this.ctx = this.canvas.getContext("2d");

    // Header offset and bounding box geometry
    const dvTopHeader = document.querySelector(".top-header-wrap");
    const margin = 0.55;
    const side_margin = 0.35;
    const bottom_margin = 0.94;

    // Primary patient bounding box
    this.patient_box = {
      x: this.canvas.width * side_margin,
      y: this.canvas.height * margin + dvTopHeader.offsetHeight,
      width: this.canvas.width * (1 - 2 * side_margin),
      height:
        (this.canvas.height - dvTopHeader.offsetHeight) * (1 - bottom_margin),
    };
    const extended_margin = 0.525;
    const extended_side_margin = 0.3;
    const extended_bottom_margin = 0.89;
    this.extended_patient_box = {
      x: this.canvas.width * extended_side_margin,
      y: this.canvas.height * extended_margin + dvTopHeader.offsetHeight,
      width: this.canvas.width * (1 - 2 * extended_side_margin),
      height:
        (this.canvas.height - dvTopHeader.offsetHeight) *
        (1 - extended_bottom_margin),
    };

    // Track if patient is in the bounding box
    this.is_patient_in_box_status = false;

    // Load shoe print image for bounding box
    this.shoe_print_img = new Image();
    this.shoe_print_img.src = shoePrintSrc;
    this.shoe_print_loaded = false;
    this.shoe_print_img.onload = () => {
      this.shoe_print_loaded = true;
    };
  }

  /**
   * Draws a horizontal line across the canvas at the given point.y coordinate.
   * Used for visualizing references such as sitting/standing thresholds.
   * @param {object} point - An object with x (ignored) and y (number) values.
   */
  mark_lines_onCanvas(point) {
    this.ctx.beginPath();
    this.ctx.moveTo(
      Math.floor(this.canvas.width / 2) - 100,
      Math.floor(point.y)
    );
    this.ctx.lineTo(
      Math.floor(this.canvas.width / 2) + 100,
      Math.floor(point.y)
    );
    this.ctx.lineWidth = 5;
    this.ctx.strokeStyle = "#FF0000";
    this.ctx.stroke();
    this.ctx.closePath();
    this.ctx.restore();
  }

  /**
   * Checks if the standing head reference has already been established.
   * @returns {boolean} True if a standing head reference is detected, else false.
   */
  stand_head_detected() {
    return this.__stand_head_detected;
  }

  /**
   * Verifies if the user is in a standing posture for the first time and
   * sets the standing reference point if so.
   * @param {object|null} pose - The user's pose object, containing keypoints and posture info.
   */
  is_standing(pose) {
    if (this.__stand_head_detected || pose == null) return;
    this.__detect_stand_head(pose);
  }

  /**
   * Internal method to detect a standing head reference. If the user is standing,
   * sets the standing_reference_point and marks stand_head_detected as true.
   * @param {object} pose - The user's pose object.
   * @returns {boolean} True if the standing head reference is established, otherwise false.
   */
  __detect_stand_head(pose) {
    const nose = pose.nose;
    const right_shoulder = pose.rightShoulder;
    const left_shoulder = pose.leftShoulder;

    // If the pose wrapper indicates a standing posture
    if (pose.is_standing()) {
      const standing_shoulder_position = Math.max(
        left_shoulder.y,
        right_shoulder.y
      );
      this.standing_reference_point =
        nose.y + (standing_shoulder_position - nose.y) / 2;
      this.__stand_head_detected = true;
      return true;
    }
    return false;
  }

  /**
   * Determines if the user is fully seated by analyzing femur-to-tibia length ratio.
   * @param {object|null} pose - The user's pose object.
   * @returns {boolean} True if fully seated, otherwise false.
   */
  is_fully_seated(pose) {
    if (pose == null || !pose.is_sitting()) {
      return false;
    }
    // Compare current ratio with a stored ratio to ensure user remains in a seated posture
    let femur2tibia_ratio = pose.femurLength / pose.tibiaLength;
    if (
      femur2tibia_ratio < 0.5 &&
      this.patient_femur_2_tibia_ratio > femur2tibia_ratio
    ) {
      this.patient_femur_2_tibia_ratio = femur2tibia_ratio;
      return false;
    }
    return true;
  }

  /**
   * Checks if the user is transitioning from sitting to standing by comparing the
   * user's eyeline with the known sitting reference point.
   * @param {object|null} pose - The user's pose object.
   * @returns {boolean} True if the user is above the sitting reference point, indicating a stand transition.
   */
  is_transitioning_to_stand(pose) {
    if (pose == null) {
      return false;
    }
    return pose.eyeLevel < this.sitting_reference_point;
  }

  /**
   * Checks if the user is currently sitting by calling pose.is_sitting().
   * @param {object|null} pose - The user's pose object.
   * @returns {boolean} True if the user is sitting, otherwise false.
   */
  is_sitting(pose) {
    if (pose == null) {
      return false;
    }
    return pose.is_sitting();
  }

  /**
   * Records the user's sitting reference point by approximating it from their nose and eyeline.
   * This is used to consistently identify when a user is seated in subsequent frames.
   * @param {object|null} pose - The user's pose object.
   */
  record_sitting_position(pose) {
    if (pose == null) {
      return;
    }
    const nose = pose.nose;
    // Use the difference between the nose and the eyeline to help define a lower seat reference
    this.sitting_reference_point =
      pose.eyeLevel - 2 * Math.abs(nose.y - pose.eyeLevel);
    // Add a small offset to ensure a gap between sitting and standing references
    this.sitting_reference_point -=
      Math.abs(this.sitting_reference_point - this.standing_reference_point) *
      0.1;
  }

  /**
   * Main logic for counting 5x sit-to-stand reps. Checks if user transitions from
   * sitting <-> standing by comparing current eyeline with stored reference points.
   * @param {object} pose - The user's pose object.
   */
  __start_5x(pose) {
    if (this.calibration_error_occured) {
      if (pose.eyeLevel > this.sitting_reference_point) {
        this.last_position = Position.Sitting;
      } else if (
        pose.eyeLevel < this.sitting_reference_point &&
        this.last_position == Position.Sitting
      ) {
        this.individual_rep_start_time_current = Date.now();
        this.calibration_error_occured = false;
      }
      return;
    }

    // If eye level is above the standing reference, user is fully upright
    if (pose.eyeLevel < this.standing_reference_point) {
      this.patient_stood_up = true;
    }

    // If user has risen above the sitting reference after previously being seated
    if (
      pose.eyeLevel < this.sitting_reference_point &&
      this.patient_in_sitting_region === true
    ) {
      this.patient_in_sitting_region = false;
      this.patient_stood_up = false;
    }

    // If user is again below the sitting reference => they've sat down
    if (pose.eyeLevel > this.sitting_reference_point) {
      // Check if they've just switched from standing to sitting
      if (this.patient_in_sitting_region === false) {
        this.patient_in_sitting_region = true;
        if (!this.patient_stood_up) {
          this.sitting_position[this.sitting_count] = pose.eyeLevel;
          this.individual_rep_start_time_current = Date.now();
        }
      }

      // If the user has performed a sit and we haven't hit 5 sits yet
      if (this.sitting_count < 5 && this.standing_count !== 0) {
        if (this.last_position !== Position.Sitting) {
          // Update the position to sitting
          this.last_position = Position.Sitting;
          this.sitting_count++;
        }

        // If user is sitting deeper or we've just captured a new rep
        if (
          !this.sitting_position[this.sitting_count] ||
          pose.eyeLevel > this.sitting_position[this.sitting_count]
        ) {
          if (this.patient_stood_up) {
            this.sitting_position[this.sitting_count] = pose.eyeLevel;
            this.individual_rep_end_time = Date.now();

            // The time for the current rep is difference from previous rep start
            this.individual_rep[this.sitting_count - 1] =
              this.individual_rep_end_time -
              this.individual_rep_start_time_previous;

            // Prepare for next rep
            this.individual_rep_start_time_current = Date.now();
            this.assessment_end_time = this.individual_rep_start_time_current;
          }
        }
      }
    }
    // If user is standing (above standing reference)
    else if (pose.eyeLevel < this.standing_reference_point) {
      console.log(
        "Standing count=" +
          this.standing_count +
          ",  Sitting count=" +
          this.sitting_count
      );
      if (this.standing_count < 5) {
        if (this.last_position !== Position.Standing) {
          this.last_position = Position.Standing;
          this.standing_count++;
        }

        // Capture the highest stand position
        if (
          !this.standing_position[this.standing_count] ||
          pose.eyeLevel < this.standing_position[this.standing_count]
        ) {
          this.standing_position[this.standing_count] = pose.eyeLevel;
          this.standing_time[this.sitting_count] = Date.now();

          // If a rep start is recorded, set it as the previous for the next rep
          if (this.individual_rep_start_time_current) {
            this.individual_rep_start_time_previous =
              this.individual_rep_start_time_current;
          }
        }
      }
    }
  }

  /**
   * Draws the main bounding box on the canvas to show where the user is expected to be.
   *
   * TODO: Refactor into a shared BoundingBoxRenderer utility class.
   * The drawing logic (roundRect, fill color, shoe print image, dotted border) is
   * duplicated across gait.js, tug.js, mCTSIB.js, and sitStandProcessing.js.
   * Each assessment calculates box coordinates differently (gait uses start_line_extended_cv,
   * TUG uses create_patient_box_at_distance with 3ft/13ft, mCTSIB uses 6ft, sitStandProcessing
   * uses fixed margins), but the rendering is identical. A shared class with a
   * draw(ctx, x, y, width, height, isPatientInBox) method would eliminate this duplication.
   */
  draw_patient_box() {
    this.ctx.beginPath();
    this.ctx.lineWidth = 5;
    this.ctx.fillStyle = "rgba(150,75,0,0.75)";

    const boxX = this.patient_box.x;
    const boxY = this.patient_box.y;
    const boxW = this.patient_box.width;
    const boxH = this.patient_box.height + 10;

    this.ctx.roundRect(boxX, boxY, boxW, boxH, 20);
    this.ctx.fill();

    // Draw shoe print image centered in the box
    if (this.shoe_print_loaded) {
      const imgAspect = this.shoe_print_img.width / this.shoe_print_img.height;
      const boxAbsHeight = Math.abs(boxH);
      const imgHeight = boxAbsHeight * 0.8;
      const imgWidth = imgHeight * imgAspect;
      const imgX = boxX + (boxW - imgWidth) / 2;
      const imgY = boxY + (boxH - imgHeight) / 2;
      this.ctx.globalAlpha = 0.85;
      this.ctx.drawImage(this.shoe_print_img, imgX, imgY, imgWidth, imgHeight);
      this.ctx.globalAlpha = 1.0;
    }

    // Dotted border lines
    this.ctx.lineWidth = 5;
    this.ctx.setLineDash([10, 5]);
    this.ctx.strokeStyle = "rgba(0,0,0,0.75)";
    this.ctx.stroke();
    this.ctx.setLineDash([]);
  }

  /**
   * Draws the main bounding box in green if the user is detected within it.
   */
  draw_patient_box_green() {
    this.ctx.beginPath();
    this.ctx.lineWidth = 5;
    this.ctx.fillStyle = "rgba(0,128,0,0.75)";

    const boxX = this.patient_box.x;
    const boxY = this.patient_box.y;
    const boxW = this.patient_box.width;
    const boxH = this.patient_box.height + 10;

    this.ctx.roundRect(boxX, boxY, boxW, boxH, 20);
    this.ctx.fill();

    // Draw shoe print image centered in the box
    if (this.shoe_print_loaded) {
      const imgAspect = this.shoe_print_img.width / this.shoe_print_img.height;
      const boxAbsHeight = Math.abs(boxH);
      const imgHeight = boxAbsHeight * 0.8;
      const imgWidth = imgHeight * imgAspect;
      const imgX = boxX + (boxW - imgWidth) / 2;
      const imgY = boxY + (boxH - imgHeight) / 2;
      this.ctx.globalAlpha = 0.85;
      this.ctx.drawImage(this.shoe_print_img, imgX, imgY, imgWidth, imgHeight);
      this.ctx.globalAlpha = 1.0;
    }

    // Dotted border lines
    this.ctx.lineWidth = 5;
    this.ctx.setLineDash([10, 5]);
    this.ctx.strokeStyle = "rgba(0,0,0,0.75)";
    this.ctx.stroke();
    this.ctx.setLineDash([]);
  }
}

export default SitStandProcessing;
